import { useEffect, useState } from "react";

import { createFileRoute } from "@tanstack/react-router";

import BackButton from "@/components/back-button";
import { useAuth } from "@/context/auth";

import { PaymentsPanel } from "./-components/payments-panel";
import { PersonalInfoPanel } from "./-components/personal-info-panel";
import { WorkExperiencePanel } from "./-components/work-experience-panel";
import { AvailabilityPanel } from "./-components/availability-panel";
import { BusinessProfileSettings } from "./-components/business-profile-settings";
import { LanguageSettings } from "./-components/language-settings";
import { CommunicationPreferencesPanel } from "./-components/communication-preferences-panel";
import { TermsPrivacyPanel } from "./-components/terms-privacy-panel";
import { BillingPanel } from "./-components/billing-panel";

import type { SettingsSectionKey } from "./-components/settings-nav";
import { getSettingsSections, SettingsNav } from "./-components/settings-nav";
import { QueryKeys } from "@/queries/queryKeys";
import { getBankAccount } from "@/queries/api/bankAccountApi";
import { useBillingEnabled } from "@/queries/hooks/billingHooks";

export const Route = createFileRoute("/app/$role/_app/settings/")({
  validateSearch: (search) => ({
    tab: (search.tab as string) || undefined,
  }),
  loader: ({ context }) => {
    context.queryClient.ensureQueryData({
      queryKey: [QueryKeys.BANK_ACCOUNT],
      queryFn: getBankAccount,
    });
  },
  component: AccountSettingsPage,
});

export default function AccountSettingsPage() {
  const auth = useAuth();
  const role = auth.user?.role || "worker";
  const search = Route.useSearch();
  // Billing tab only for opted-in owners (flag + billing/status.enabled).
  const { enabled: billingEnabled } = useBillingEnabled();
  const sections = getSettingsSections(role, { billingEnabled });
  const sectionKeys = sections.map((s) => s.key);
  const initialTab =
    typeof search.tab === "string" &&
    sectionKeys.includes(search.tab as SettingsSectionKey)
      ? (search.tab as SettingsSectionKey)
      : role === "worker"
        ? "personal"
        : "business-config";
  const [selected, setSelected] = useState<SettingsSectionKey>(initialTab);

  // Keep the active panel in sync when navigated here with a ?tab= param
  // (e.g. the Availability shortcut in the floating dock) while already mounted.
  useEffect(() => {
    const validKeys = getSettingsSections(role, { billingEnabled }).map(
      (s) => s.key,
    );
    if (
      typeof search.tab === "string" &&
      validKeys.includes(search.tab as SettingsSectionKey)
    ) {
      setSelected(search.tab as SettingsSectionKey);
    }
  }, [search.tab, role, billingEnabled]);

  const renderPanel = () => {
    switch (selected) {
      case "personal":
        return role === "worker" ? <PersonalInfoPanel /> : null;
      case "work-experience":
        return <WorkExperiencePanel />;
      case "availability":
        return <AvailabilityPanel />;
      case "business-config":
        return <BusinessProfileSettings />;
      case "payments":
        return <PaymentsPanel />;
      case "billing":
        return billingEnabled ? <BillingPanel /> : null;
      case "communication":
        return <CommunicationPreferencesPanel />;
      case "terms":
        return <TermsPrivacyPanel />;
      case "language":
        return <LanguageSettings />;
      default:
        return null;
    }
  };

  return (
    // Fixed width: AppShell's <main> is a mx-auto flex item, so it shrinks to
    // fit its content — without this, the page resized with every panel.
    <div className="flex flex-col h-full w-[1264px] max-w-full px-3 sm:px-6 md:px-8 pt-4 sm:pt-6 md:pt-8">
      <BackButton />
      <div className="flex gap-3 sm:gap-5 md:gap-8 mt-4 md:mt-7 pb-12 md:pb-24 w-full max-w-[1200px]">
        {/* Left: Navigation */}
        <SettingsNav
          selected={selected}
          onSelect={setSelected}
          role={role}
          billingEnabled={billingEnabled}
        />
        {/* Right: Details */}
        <section className="flex-1 min-w-0 bg-white rounded-2xl shadow p-4 sm:p-6 md:p-8 min-h-[400px] md:min-h-[600px] overflow-hidden">
          {renderPanel()}
        </section>
      </div>
    </div>
  );
}
